import * as React from 'react'
import * as Api from 'src/api'
import { useDebouncedLoadOptions } from 'src/helpers/hooks'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import AsyncSelectInput from 'src/views/components/forms/formik/AsyncSelectInput'
import { type Option } from 'src/views/components/forms/formik/SelectInput'

export function FacsProgramsSelectField({
  headers,
  degree,
  facultyIds,
  onOptionsLoaded,
  knownLabels,
}: {
  readonly headers: AuthenticatedHeaders
  readonly degree: string
  readonly facultyIds: readonly string[]
  readonly onOptionsLoaded?: (options: readonly Option[]) => void
  readonly knownLabels?: Readonly<Record<string, string>>
}): JSX.Element {
  const t = useTranslatable()

  const loadOptions = useDebouncedLoadOptions<Option>(
    React.useCallback(
      async (q: string) => {
        const parsedFacultyIds = facultyIds.map((id) => Number.parseInt(id, 10)).filter((n) => Number.isFinite(n))
        const result = await Api.getAdministratorPrograms({
          headers,
          query: {
            page: 1,
            perPage: 100,
            status: 1,
            ...(q.length > 0 ? { name: q } : {}),
            ...(degree !== '' ? { degree: Number.parseInt(degree, 10) } : {}),
            ...(parsedFacultyIds.length > 0 ? { facultyIds: parsedFacultyIds } : {}),
          },
        })
        const seen = new Set<string>()
        const options: Option[] = []
        for (const program of result.data) {
          if (!seen.has(program.id)) {
            seen.add(program.id)
            options.push({ value: program.id, label: program.name })
          }
        }
        onOptionsLoaded?.(options)
        return options
      },
      [headers, degree, facultyIds, onOptionsLoaded]
    )
  )

  const cacheKey = `${facultyIds.join(',')}_${degree}`

  return (
    <AsyncSelectInput
      key={cacheKey}
      name="facsProgramIds"
      label={t(['პროგრამები (ზოგადი მოდული)', 'Programs (General Module)'])}
      isMulti
      storeValueOnly
      closeMenuOnSelect={false}
      cacheOptions
      loadOptions={loadOptions}
      placeholder={t(['აირჩიეთ პროგრამები', 'Select programs'])}
      defaultOptions
      initialKnownLabels={knownLabels}
    />
  )
}
